import React from 'react'
import { set, isEqual } from 'lodash'
import { useNavigate } from 'react-router-dom'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import api from '../../api'
import { DEBUG_MODE } from '../../utils'
import { getSaleProductsFromCartEntries } from '../../utils/sale'
import { cartClearCart } from '../../reducers/cartReducer'
import { setSelectedLocation } from '../../reducers/locationReducer'
import { SALE_PAYMENT_TYPES } from '../../constants'
import CheckoutFormDiscountCode from './CheckoutFormDiscountCode'
import CheckoutFormSubmit from './CheckoutFormSubmit'
import CheckoutFormPaymentMethod from './CheckoutFormPaymentMethod'
import CheckoutFormContactInformation from './CheckoutFormContactInformation'
import CheckoutFormLocationSelect from './CheckoutFormLocationSelect'
import CheckoutFormShipmentMethod from './CheckoutFormShipmentMethod'

const DEBUG_PREFIX = '[CHECKOUT_FORM]'
const DELAY = 500

const CheckoutForm = ({
    discountCodeButtonLabel,
    discountCodeLabel,
    noShipmentLabel,
    showDiscountCodeInput,
}) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const cartEntries = useSelector(state => state.cart.entries, shallowEqual)
    const session = useSelector(state => state.session, shallowEqual)
    const locations = useSelector(state => state.location.locations)
    const selectedLocationId = useSelector(
        state => state.location.selectedLocationId,
    )
    const [personalInformation, setPersonalInformation] = React.useState({
        name: '',
        email: '',
        phone: '',
        phonePrefix: '+598',
    })
    const [checkoutData, setCheckoutData] = React.useState({
        onSitePayment: {
            payingWithCashAmount: '',
        },
        discounts: [],
    })

    // Loading states
    const [isCalculatingPrices, setIsCalculatingPrices] = React.useState(false)
    const [isCheckingShipment, setIsCheckingShipment] = React.useState(false)
    const [isSubmitting, setIsSubmitting] = React.useState(false)
    const [isProcessingDiscountCode, setIsProcessingDiscountCode] =
        React.useState(false)
    const isLoading = isCalculatingPrices || isCheckingShipment || isSubmitting

    // Error handling
    const [formErrorMessages, setFormErrorMessages] = React.useState({})
    const [submitError, setSubmitError] = React.useState()
    const [cashAmountError, setCashAmountError] = React.useState()
    const [discountCodeSubmitError, setDiscountCodeSubmitError] =
        React.useState()

    // Shipment
    const [shipmentMethod, setShipmentMethod] = React.useState()
    const [shipmentError, setShipmentError] = React.useState()

    // Prices
    const [discountCode, setDiscountCode] = React.useState()
    const [shipmentCost, setShipmentCost] = React.useState(0)
    const [subTotal, setSubTotal] = React.useState(0)
    const [total, setTotal] = React.useState(0)

    const me = React.useRef()
    const shipmentZoneFetchTimeout = React.useRef()
    const priceCalculationTimeout = React.useRef()
    const isAuthenticated = !!session?.user?._id
    const isDeliver = shipmentMethod === 'deliver'

    // Location
    const selectedLocation = locations.find(
        location => location.id === selectedLocationId,
    )
    const isSelectedLocationOpen = selectedLocation?.isOpenNow

    // Submit state
    const error =
        (isDeliver &&
            !(checkoutData.street && checkoutData.doorNumber) &&
            'Debes completar la dirección de envío') ||
        (isDeliver && shipmentError) ||
        cashAmountError ||
        (!isAuthenticated &&
            !personalInformation.email &&
            !personalInformation.phone &&
            'Debes ingresar correo electrónico o teléfono') ||
        (selectedLocationId &&
            !isSelectedLocationOpen &&
            'El local seleccionado se encuentra cerrado')
    const isSubmitDisabled =
        (isDeliver && !(checkoutData.street && checkoutData.doorNumber)) ||
        error ||
        isLoading ||
        !cartEntries.length ||
        (!isAuthenticated &&
            !(
                personalInformation.name &&
                (personalInformation.email || personalInformation.phone)
            )) ||
        !checkoutData.paymentType ||
        (checkoutData.paymentType === SALE_PAYMENT_TYPES.ONSITE &&
            !checkoutData.onSitePayment.method) ||
        !isSelectedLocationOpen

    React.useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    React.useEffect(() => {
        setFormErrorMessages({})
    }, [personalInformation])

    React.useEffect(() => {
        setCheckoutData(checkoutData => ({
            ...checkoutData,
            location: selectedLocationId,
        }))
    }, [selectedLocationId])

    React.useEffect(() => {
        if (DEBUG_MODE) {
            console.log(DEBUG_PREFIX, 'Updated form:', checkoutData)
        }
    }, [checkoutData])

    const calculatePrices = async checkout => {
        try {
            setFormErrorMessages({})
            setSubmitError()
            setIsCalculatingPrices(true)
            const url = new URL('sales/calculate', api.defaults.baseURL)
            const res = await api.post(decodeURIComponent(url), checkout)
            if (DEBUG_MODE) {
                console.log(DEBUG_PREFIX, 'Calculated sale', res.data.sale)
            }
            const { sale } = res.data
            if (!isEqual(sale.discounts, checkout.discounts)) {
                setCheckoutData(checkoutData => ({
                    ...checkoutData,
                    discounts: sale.discounts,
                }))
            }
            setSubTotal(sale.subTotalPriceSnapshot)
            setShipmentCost(sale.shipmentCost)
            setTotal(sale.totalPriceSnapshot)
        } catch (error) {
            console.error(DEBUG_PREFIX, error)
        } finally {
            setIsCalculatingPrices(false)
        }
    }

    React.useEffect(() => {
        if (priceCalculationTimeout.current) {
            clearTimeout(priceCalculationTimeout.current)
        }
        priceCalculationTimeout.current = setTimeout(() => {
            const sale = {
                ...checkoutData,
                products: getSaleProductsFromCartEntries(cartEntries),
            }
            calculatePrices(sale)
        }, DELAY)
    }, [checkoutData, cartEntries])

    React.useEffect(() => {
        if (shipmentZoneFetchTimeout.current) {
            clearTimeout(shipmentZoneFetchTimeout.current)
        }
        if (!isDeliver || !checkoutData.street || !checkoutData.doorNumber) {
            return
        }
        shipmentZoneFetchTimeout.current = setTimeout(async () => {
            try {
                setShipmentError()
                setIsCheckingShipment(true)
                const url = new URL(
                    'webstore/shipment-costs/search',
                    api.defaults.baseURL,
                )
                const filters = new URLSearchParams({
                    street: checkoutData.street,
                    doorNumber: checkoutData.doorNumber,
                    city: checkoutData.city,
                    country: checkoutData.country,
                })
                if (checkoutData.zipCode) {
                    filters.append('zipCode', checkoutData.zipCode)
                }
                if (checkoutData.location) {
                    filters.append('location', checkoutData.location)
                }
                url.search = filters
                const res = await api.get(decodeURIComponent(url))
                const { webstoreShipmentCost } = res.data
                if (DEBUG_MODE) {
                    console.log(
                        DEBUG_PREFIX,
                        'Matching Shipment Cost:',
                        webstoreShipmentCost,
                    )
                }
                if (!webstoreShipmentCost) {
                    setShipmentError(
                        noShipmentLabel ||
                            'El local seleccionado no realiza entregas a la dirección',
                    )
                }
            } catch (error) {
                console.error(
                    DEBUG_PREFIX,
                    'Error getting shipment cost',
                    error.response?.data,
                )
                setShipmentError(
                    'No pudimos encontrar la dirección que ingresaste',
                )
            } finally {
                setIsCheckingShipment(false)
            }
        }, DELAY)
    }, [
        noShipmentLabel,
        isDeliver,
        checkoutData.street,
        checkoutData.doorNumber,
        checkoutData.zipCode,
        checkoutData.city,
        checkoutData.country,
        checkoutData.location,
    ])

    React.useEffect(() => {
        setCashAmountError()
        if (
            checkoutData.onSitePayment.payingWithCashAmount?.length &&
            checkoutData.onSitePayment.method === 'CASH' &&
            Number(checkoutData.onSitePayment.payingWithCashAmount) < total
        ) {
            setCashAmountError(
                'El monto de efectivo no puede ser menor al total',
            )
        }
    }, [
        checkoutData.onSitePayment.method,
        checkoutData.onSitePayment.payingWithCashAmount,
        total,
    ])

    const handlePersonalInformationChange = (key, value) => {
        setPersonalInformation(personalInformation => ({
            ...personalInformation,
            [key]: value,
        }))
    }

    const handleLocationChange = event => {
        dispatch(setSelectedLocation(event.target.value))
    }

    const handleCheckoutChange = React.useCallback((path, value) => {
        setCheckoutData(checkoutData => {
            const newCheckoutData = { ...checkoutData }
            set(newCheckoutData, path, value)
            return newCheckoutData
        })
    }, [])

    const handleShipmentMethodChange = shipmentMethod => {
        setShipmentMethod(shipmentMethod)
        if (shipmentMethod !== 'deliver') {
            setShipmentError()
        }
    }

    const handleDiscountCodeChange = event => {
        setDiscountCodeSubmitError()
        setDiscountCode(event.target.value)
    }

    const handleDiscountCodeRemove = discountId => {
        setCheckoutData(checkoutData => {
            checkoutData.discounts = checkoutData.discounts.filter(
                id => id !== discountId,
            )
            return { ...checkoutData }
        })
    }

    const handleDiscountCodeSubmit = async () => {
        try {
            setIsProcessingDiscountCode(true)
            const url = new URL('discounts', api.defaults.baseURL)
            const filters = new URLSearchParams({
                name: discountCode,
                isActive: true,
            })
            url.search = filters
            const res = await api.get(decodeURIComponent(url))
            const { discounts } = res.data
            if (DEBUG_MODE) {
                console.log(DEBUG_PREFIX, 'Discount search results:', discounts)
            }
            if (discounts?.length) {
                setCheckoutData(checkoutData => {
                    checkoutData.discounts.push(discounts[0].id)
                    return { ...checkoutData }
                })
                setDiscountCode('')
                alert('Descuento aplicado')
            } else {
                throw new Error('Código de descuento inválido')
            }
        } catch (error) {
            setDiscountCodeSubmitError(error.message)
        } finally {
            setIsProcessingDiscountCode(false)
        }
    }

    const handleCheckout = async event => {
        try {
            event.preventDefault()
            setSubmitError()
            setIsSubmitting(true)

            let contactId
            const phoneNumber = personalInformation.phone
                ? personalInformation.phonePrefix + personalInformation.phone
                : null

            // Check authentication status
            if (!session.user._id) {
                // Create Contact with the given details (or use an existing one)
                let alreadyExistingContact
                if (personalInformation.email || phoneNumber) {
                    const isSearchByPhone = !personalInformation.email
                    let getContactEndpoint = `contacts?`
                    if (isSearchByPhone) {
                        // Use "$" for end-with kind of search
                        // this is not ideal but search is impossible without it
                        getContactEndpoint += `phone=$${encodeURIComponent(
                            phoneNumber,
                        )}`
                    } else {
                        getContactEndpoint += `email=${encodeURIComponent(
                            personalInformation.email,
                        )}`
                    }
                    // First check if there is an email matching
                    const alreadyExistingContactRes =
                        await api.get(getContactEndpoint)
                    const results = alreadyExistingContactRes.data.contacts
                    if (results?.length) {
                        if (isSearchByPhone) {
                            for (
                                let index = 0;
                                index < results.length;
                                index++
                            ) {
                                const result = results[index]
                                // Look for exact phone match
                                if (result.phone === phoneNumber) {
                                    alreadyExistingContact = result
                                    break
                                }
                            }
                        } else {
                            // If there is a matching, assume it is the same user
                            alreadyExistingContact = results[0]
                        }
                    }
                }
                if (alreadyExistingContact) {
                    const getFullContactRes = await api.get(
                        `contacts/` + alreadyExistingContact.id,
                    )
                    if (getFullContactRes.data.contact) {
                        alreadyExistingContact = getFullContactRes.data.contact
                    }
                    // Populate Contact information that might be missing from original
                    // Contact document, like phone number, name, etc.
                    const isNameDifferent =
                        alreadyExistingContact.name !== personalInformation.name
                    const isPhoneDifferent =
                        phoneNumber &&
                        alreadyExistingContact.phone !== phoneNumber
                    if (isNameDifferent || isPhoneDifferent) {
                        const updatedContactInformation = {}
                        if (isNameDifferent) {
                            updatedContactInformation.name =
                                personalInformation.name
                        }
                        if (isPhoneDifferent) {
                            updatedContactInformation.phone = phoneNumber
                        }
                        if (DEBUG_MODE) {
                            console.log(
                                DEBUG_PREFIX,
                                'Updating Contact with:',
                                updatedContactInformation,
                            )
                        }
                        if (alreadyExistingContact.user?.id) {
                            await api.put(
                                `users/${alreadyExistingContact.user.id}`,
                                updatedContactInformation,
                            )
                        } else {
                            await api.put(
                                `contacts/${alreadyExistingContact._id}`,
                                updatedContactInformation,
                            )
                        }
                    }
                    contactId = alreadyExistingContact._id
                } else {
                    const contactData = {
                        ...personalInformation,
                        phone: phoneNumber,
                    }
                    if (DEBUG_MODE) {
                        console.log(
                            DEBUG_PREFIX,
                            'Creating Contact with:',
                            contactData,
                        )
                    }
                    const contactRes = await api.post('contacts', contactData)
                    contactId = contactRes.data.contact._id
                }
            } else {
                contactId = session.user.contact._id || session.user.contact
            }

            const saleBasePayload = {
                contact: contactId,
                discounts: checkoutData.discounts,
                location: checkoutData.location,
                products: getSaleProductsFromCartEntries(cartEntries),
                paymentType: checkoutData.paymentType,
                onSitePayment:
                    checkoutData.paymentType === SALE_PAYMENT_TYPES.ONSITE
                        ? {
                              method: checkoutData.onSitePayment.method,
                              payingWithCashAmount:
                                  checkoutData.onSitePayment
                                      .payingWithCashAmount,
                          }
                        : undefined,
            }

            if (isDeliver) {
                saleBasePayload.street = checkoutData.street
                saleBasePayload.doorNumber = checkoutData.doorNumber
                saleBasePayload.apartment = checkoutData.apartment
                saleBasePayload.zipCode = checkoutData.zipCode
                saleBasePayload.city = checkoutData.city
                saleBasePayload.country = checkoutData.country
                saleBasePayload.notes = checkoutData.notes
            }

            if (DEBUG_MODE) {
                console.log(DEBUG_PREFIX, 'Sale to create:', saleBasePayload)
            }
            const res = await api.post('/sales', saleBasePayload)

            if (checkoutData.paymentType === SALE_PAYMENT_TYPES.ONLINE) {
                // Redirect to MP checkout
                window.location.assign(res.data.paymentLink)
            } else if (checkoutData.paymentType === SALE_PAYMENT_TYPES.ONSITE) {
                // If is unauthenticated purhcase, clean LS cart
                if (!session.user._id) {
                    dispatch(cartClearCart())
                }
                navigate(`/payment-success?purchase_id=${res.data.sale._id}`)
            }
        } catch (error) {
            if (DEBUG_MODE) {
                console.error(DEBUG_PREFIX, error)
            }
            let errorMessage = error.response?.data?.message
            if (error.response?.data?.errors) {
                errorMessage = ''
                for (const errorKey in error.response.data.errors) {
                    const errorValue = error.response.data.errors[errorKey]
                    if (errorValue?.message) {
                        errorMessage += '\n' + errorValue.message
                    }
                }
            }
            setSubmitError(errorMessage)
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <form ref={me} className='checkout-form' onSubmit={handleCheckout}>
            {!session.user._id ? (
                <CheckoutFormContactInformation
                    contactInformation={personalInformation}
                    formErrorMessages={formErrorMessages}
                    onChange={handlePersonalInformationChange}
                />
            ) : null}
            <CheckoutFormLocationSelect
                checkout={checkoutData}
                onChange={handleLocationChange}
            />
            <CheckoutFormShipmentMethod
                checkout={checkoutData}
                onAddressChange={handleCheckoutChange}
                onShipmentMethodChange={handleShipmentMethodChange}
                shipmentMethod={shipmentMethod}
                showAddressForm={isDeliver}
            />
            <CheckoutFormPaymentMethod
                checkout={checkoutData}
                onChange={handleCheckoutChange}
            />
            {showDiscountCodeInput ? (
                <CheckoutFormDiscountCode
                    buttonLabel={discountCodeButtonLabel}
                    discountCode={discountCode}
                    onChange={handleDiscountCodeChange}
                    onSubmit={handleDiscountCodeSubmit}
                    placeholder={discountCodeLabel}
                    error={discountCodeSubmitError}
                    isLoading={isProcessingDiscountCode}
                />
            ) : null}
            <CheckoutFormSubmit
                checkout={checkoutData}
                disabled={isSubmitDisabled}
                error={error}
                isLoading={isLoading}
                onRemoveDiscount={handleDiscountCodeRemove}
                shipmentCost={shipmentCost}
                showShipmentCost={isDeliver && typeof shipmentCost === 'number'}
                subTotal={subTotal}
                submitError={submitError}
                total={total}
            />
        </form>
    )
}

export default CheckoutForm
